import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import SignatureCanvas from 'react-signature-canvas';
import { Container, Table, Card } from '@themesberg/react-bootstrap';
import Layout from '../components/Layout';
import { BASE_URL } from '../constants';

const RefugeeSelfCheckInOut = () => {
  const [availability, setAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [isCurrentlyAvailable, setIsCurrentlyAvailable] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationRequested, setLocationRequested] = useState(false);

  const userData = JSON.parse(localStorage.getItem('userData')) || {};
  const refugeeId = userData?.id;
  const shelterId = userData?.shelter?.id;

  const signaturePad = useRef(null);

  
  const saveSignatureAutomatically = () => {
    if (signaturePad.current) {
      const signature = signaturePad.current.toDataURL();
      setSignatureData(signature);
    }
  };

  const fetchAvailability = useCallback(async () => {
    if (!refugeeId) {
      setError('User ID not provided');
      setLoadingAvailability(false);
      return;
    }
  
    try {
      const { data } = await axios.get(
        `${BASE_URL}/refugees/${refugeeId}/availability`
      );
  
      if (data?.availability?.length > 0) {
        const availabilityData = data.availability;
        const lastAction = availabilityData[0];
        setAvailability(availabilityData);
        const isCheckedIn = lastAction.checkDetails?.checkOut === null;
        setIsCurrentlyAvailable(isCheckedIn);
      } else {
        setAvailability([]);
        setIsCurrentlyAvailable(false);
      }
    } catch (err) {
      console.error('Error fetching availability:', err);
    } finally {
      setLoadingAvailability(false);
    }
  }, [refugeeId]);
  
  
  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);
  

useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ latitude, longitude });
          console.log("Location", location);
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setError('Please enable location services to check in or check out.');
          } else {
            setError(` Error getting location: ${error.message}`);
          }
        },
        { enableHighAccuracy: true }
      );
    } else {
      setError('Geolocation is not supported by this browser.');
    }
  }, []);


  const handleCheckInCheckOut = async () => {
    if (!signatureData) {
      setError('Signature is required');
      return;
    }
  
    setLoading(true);
    if (!location) {
      setError('Location not available');
      setLoading(false);
      return;
    }
    console.log('Current location:', location.latitude, location.longitude);
    try {
      const { data } = await axios.put(
        `${BASE_URL}/refugees/${refugeeId}/checkin-checkout`,
        {
          signature: signatureData,
          latitude: location.latitude,
          longitude: location.longitude,
          shelterId,
        }
      );
  
      const updatedAvailability = data.refugee.availability;
      const lastEntry = updatedAvailability[0];
      const isCheckedIn = lastEntry.checkDetails?.checkOut === null;
  
      setAvailability(updatedAvailability);
      setIsCurrentlyAvailable(isCheckedIn);
      setSignatureData('');
      signaturePad.current.clear();
      setSuccessMessage(data.message || 'Status updated successfully!');
      setError('');
  
      // 🔁 Fetch updated data from backend to ensure it's fresh
      fetchAvailability();
    } catch (err) {
      console.error('Error during check-in/check-out:', err);
      setError(err.response?.data?.message || 'Failed to update status');
      setSuccessMessage('');
    } finally {
      setLoading(false);
    }
  };
  
  

  const clearSignature = () => {
    signaturePad.current.clear();
    setSignatureData('');
  };
  
  return (
   
      <Layout>
              <Container className="mt-3"> 

  {!shelterId ? (
      <div className="card mt-3">
      <div className="card-body">
        <p className="card-text"> No Room assigned to you.</p>
      </div>
    </div>
  
  ) : (
    <div className="row justify-content-center">
    <div className="col-12 col-md-9 col-lg-7">
    <div className="container mt-4">
                <Card className="shadow-lg border-0 rounded">
                    <Card.Body className="p-4">
                  <h3 className="text-center mb-4 text-primary">Check-In/Check-Out</h3>
                  <div className="form-group mb-2">
                    <label htmlFor="signature" className="fw-bold mb-3">
                      Signature:
                    </label>
                    <SignatureCanvas
                      ref={signaturePad}
                      onEnd={saveSignatureAutomatically}
                      penColor="black"
                      canvasProps={{
                        className: 'signature-pad w-100',
                        height: 150,
                        style: {
                          border: '2px solid #ccc',
                          borderRadius: '5px',
                          maxWidth: '100%',
                          margin: '0 auto',
                        },
                      }}
                    />
                    <div className="d-flex justify-content-between mt-2">
                      <button onClick={clearSignature} className="btn w-100 btn-dark">
                        Clear Signature
                      </button>
                    </div>
                  </div>
  
                  <div className="row g-2 mb-4">
                    <div className="col-12">
                    {!loadingAvailability && ( 
  <button
    onClick={handleCheckInCheckOut}
    className={`btn w-100 ${isCurrentlyAvailable ? 'btn-danger' : 'btn-success'}`}
    disabled={loading}
  >
    {loading
      ? isCurrentlyAvailable
        ? 'Checking out...'
        : 'Checking in...'
      : isCurrentlyAvailable
      ? 'Check Out'
      : 'Check In'}
  </button>
)}



                    </div>
                    {error && <div className="alert alert-danger">{error}</div>}
                    {successMessage && (
                      <div className="alert alert-success">{successMessage}</div>
                    )}
                  </div>
  
                  {loading ? (
                    <div className="text-center">
                      <div className="spinner-border text-primary" role="status">
                    
                      </div>
                    </div>
                  ) : (
                    <div>
{availability.length > 0 && (
  <>
    <label htmlFor="availability" className="fw-bold mb-3">
      Availability History:
    </label>
    <Table responsive className="align-items-center table-flush text-center">
      <thead className="thead-light">
        <tr>
          <th scope="col">Date</th>
          <th scope="col">Status</th>
          <th scope="col">Signature</th>
        </tr>
      </thead>
      <tbody>
        {/* Check-Out Row for Latest Action - shown first */}
        {availability[0]?.checkDetails?.checkOutSignature && (
          <tr key={`checkout-${0}`}>
            <td>
              {new Date(availability[0].checkDetails.checkOut).toLocaleString()}
            </td>
            <td>
              <span className="badge bg-danger text-light">Checked Out</span>
            </td>
            <td>
              {availability[0].checkDetails.checkOutSignature === "Auto-Checkout" ? (
                <span className="text-muted">Automatic</span>
              ) : (
                <img
                  src={availability[0].checkDetails.checkOutSignature}
                  alt="Check-Out Signature"
                  className="img-fluid rounded"
                  style={{
                    maxWidth: '100px',
                    display: 'block',
                    margin: '0 auto',
                  }}
                />
              )}
            </td>
          </tr>
        )}

        {/* Check-In Row for Latest Action - shown second */}
        {availability[0]?.checkDetails?.checkInSignature && (
          <tr key={`checkin-${0}`}>
            <td className="col-4 col-sm-3 col-md-2">
              {new Date(availability[0].checkDetails.checkIn).toLocaleString()}
            </td>
            <td>
              <span className="badge bg-success text-light">Checked In</span>
            </td>
            <td>
              <img
                src={availability[0].checkDetails.checkInSignature}
                alt="Check-In Signature"
                className="img-fluid rounded"
                style={{
                  maxWidth: '100px',
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  </>
)}

                    </div>
                  )}
              </Card.Body>
                </Card>
                </div>
            </div>
        </div>
  )}
        
        </ Container>
      </ Layout>
    );
};

export default RefugeeSelfCheckInOut;